import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePreferences } from './PreferencesContext';
import { getCallDirection, getCallStatus } from '../utils';
import { rangeLabel, resolvePreset } from '../lib/time';

/**
 * Filter state for the Calls screen and the analytics period for everything else.
 *
 * THREE THINGS THIS FIXES
 * -----------------------
 * 1. **Drill-downs replace, they do not merge.** The old handler applied a
 *    `statFilter` from the URL with `setFilters(prev => ...)` and WITHOUT the date
 *    range, and the `total` case actively reset to today. So clicking "17 not
 *    called back" on a 30-day dashboard landed on a today-scoped page intersected
 *    with 17 call ids — legitimately zero rows — while the chips still claimed
 *    otherwise. A drill now carries its own period and replaces the whole set.
 *
 * 2. **The period is in the URL.** A filtered view is shareable, bookmarkable and
 *    survives a reload. It also means the browser Back button does what it looks
 *    like it does.
 *
 * 3. **Every filter produces a chip, and every chip's X works.** The old
 *    `activeFilters` list was hand-maintained: `vendor` was missing entirely, so a
 *    Source filter was invisible, and the date chip's remove handler had no date
 *    branch, so its X did nothing. Chips are now derived from one schema.
 */

const FiltersContext = createContext(null);

export const useFilters = () => {
  const ctx = useContext(FiltersContext);
  if (!ctx) throw new Error('useFilters must be used within FiltersProvider');
  return ctx;
};

/** Every filter key, with how it is serialised and how its chip reads. */
export const FILTER_SCHEMA = [
  { key: 'search', param: 'q', label: (v) => `Search: "${v}"` },
  { key: 'sentiment', param: 'sentiment', label: (v) => `Sentiment: ${v}` },
  { key: 'agent', param: 'agent', label: (v) => `Agent: ${v}` },
  { key: 'category', param: 'category', label: (v) => `Category: ${v}` },
  { key: 'source', param: 'source', label: (v) => `Channel: ${v}` },
  // `vendor` had no chip at all, so applying it left no visible trace.
  { key: 'vendor', param: 'vendor', label: (v) => `Provider: ${v}` },
  { key: 'inbound', param: 'direction', label: (v) => `Direction: ${getCallDirection(v)}` },
  { key: 'answered', param: 'outcome', label: (v, f) => `Outcome: ${getCallStatus(v, f.inbound)}` },
  { key: 'recorded', param: 'recorded', label: (v) => `Recording: ${v === 'true' ? 'available' : 'none'}` },
  { key: 'qualityMin', param: 'q_min', label: (v) => `Quality ≥ ${v}` },
  { key: 'qualityMax', param: 'q_max', label: (v) => `Quality ≤ ${v}` },
  { key: 'actionStatus', param: 'action', label: (v) => `Follow-up: ${v.replace(/_/g, ' ')}` },
  // The Outlook's rules (low quality, negative with no next action, unusually
  // long) measured against the selected range. The server computes the medians.
  { key: 'flagged', param: 'flagged', label: () => 'Flagged calls' },
];

const EMPTY_FILTERS = FILTER_SCHEMA.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

/**
 * The default period is LAST 7 DAYS, not today.
 *
 * `today` was the old default and it made the flagship trend chart a single point
 * with an inert gradient fill — the first thing a new customer saw. Seven days is
 * the shortest window in which a trend is a trend.
 */
const buildDefaultFilters = (timeZone) => ({
  ...EMPTY_FILTERS,
  insightFilters: {},
  themeFilter: null, // Conversation Themes click-through: { field_key, field_name, name, aliases }
  callIds: [],
  ...resolvePreset('last7days', timeZone),
});

const buildDefaultPeriod = (timeZone) => resolvePreset('last7days', timeZone);

/** Filters -> URL params. Only non-defaults are written, so URLs stay readable. */
const toSearchParams = (filters) => {
  const params = new URLSearchParams();
  FILTER_SCHEMA.forEach(({ key, param }) => {
    const v = filters[key];
    if (v) params.set(param, String(v));
  });
  if (filters.dateRange) params.set('range', filters.dateRange);
  if (filters.dateRange === 'custom') {
    if (filters.startDate) params.set('from', filters.startDate);
    if (filters.endDate) params.set('to', filters.endDate);
  }
  const insights = Object.entries(filters.insightFilters || {}).filter(([, v]) => v);
  if (insights.length) params.set('insights', JSON.stringify(Object.fromEntries(insights)));
  if (filters.themeFilter?.aliases?.length) params.set('theme', JSON.stringify(filters.themeFilter));
  // Call-id sets are drill-through payloads and can be large; they are kept out
  // of the URL and treated as session state, with the chip saying so.
  return params;
};

/** URL params -> filters. Anything unrecognised is ignored rather than trusted. */
const fromSearchParams = (params, timeZone) => {
  const next = { ...buildDefaultFilters(timeZone) };
  FILTER_SCHEMA.forEach(({ key, param }) => {
    const v = params.get(param);
    if (v) next[key] = v;
  });

  const range = params.get('range');
  if (range === 'custom') {
    const from = params.get('from');
    const to = params.get('to');
    if (from && to) {
      next.dateRange = 'custom';
      next.startDate = from;
      next.endDate = to;
    }
  } else if (range) {
    Object.assign(next, resolvePreset(range, timeZone));
  }

  const theme = params.get('theme');
  if (theme) {
    try {
      const parsed = JSON.parse(theme);
      if (parsed && Array.isArray(parsed.aliases) && parsed.field_key) next.themeFilter = parsed;
    } catch {
      /* a malformed URL should not blank the screen */
    }
  }

  const insights = params.get('insights');
  if (insights) {
    try {
      const parsed = JSON.parse(insights);
      if (parsed && typeof parsed === 'object') next.insightFilters = parsed;
    } catch {
      /* a malformed URL should not blank the screen */
    }
  }
  return next;
};

export const FiltersProvider = ({ children }) => {
  const { timeZone } = usePreferences();
  const navigate = useNavigate();
  const location = useLocation();

  const [filters, setFiltersRaw] = useState(() => buildDefaultFilters(timeZone));
  const [period, setPeriodRaw] = useState(() => buildDefaultPeriod(timeZone));
  const [currentPage, setCurrentPage] = useState(1);
  // 25 rows, not 10: the Calls table is the workhorse screen and a 10-row page
  // turns "scan the bad calls" into six page clicks.
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sort, setSort] = useState({ by: 'startTime', dir: 'desc' });

  /**
   * The tenant's timezone arrives after the settings query resolves, which can be
   * after first render. Re-resolving a PRESET range in the new zone corrects the
   * dates; a custom range is left alone because the user typed those dates.
   */
  const appliedTz = useRef(timeZone);
  useEffect(() => {
    if (appliedTz.current === timeZone) return;
    appliedTz.current = timeZone;
    setFiltersRaw((prev) =>
      prev.dateRange === 'custom' ? prev : { ...prev, ...resolvePreset(prev.dateRange, timeZone) }
    );
    setPeriodRaw((prev) =>
      prev.dateRange === 'custom' ? prev : { ...prev, ...resolvePreset(prev.dateRange, timeZone) }
    );
  }, [timeZone]);

  /**
   * Hydrate from the URL on arrival at /calls, so a shared link reproduces the
   * exact view. Only on a real navigation — writing filters back to the URL must
   * not re-trigger this and clobber in-session state such as a callIds set.
   */
  const onCallsRoute = location.pathname.startsWith('/calls') || location.pathname.startsWith('/call-records');
  const hydratedFor = useRef(null);
  useEffect(() => {
    if (!onCallsRoute) return;
    // `call` is a permalink to one call, not part of the filter set: the Calls
    // screen opens it and drops it. Counting it here would reset the recipient's
    // filters to the defaults just for following a link.
    const params = new URLSearchParams(location.search);
    params.delete('call');
    const key = params.toString() ? `?${params}` : '';
    if (hydratedFor.current === key) return;
    hydratedFor.current = key;
    if (!key) return; // a bare /calls keeps whatever the session already had
    const incoming = fromSearchParams(params, timeZone);
    setFiltersRaw((prev) => ({ ...incoming, callIds: prev.callIds, callIdsLabel: prev.callIdsLabel }));
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCallsRoute, location.search, timeZone]);

  /** Push the current filters into the URL without adding history entries. */
  const syncUrl = useCallback(
    (next) => {
      if (!onCallsRoute) return;
      const params = toSearchParams(next);
      const search = params.toString();
      if (search === location.search.replace(/^\?/, '')) return;
      hydratedFor.current = search ? `?${search}` : '';
      navigate({ pathname: location.pathname, search }, { replace: true });
    },
    [navigate, location.pathname, location.search, onCallsRoute]
  );

  const setFilters = useCallback(
    (updater) => {
      setFiltersRaw((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        syncUrl(next);
        return next;
      });
      setCurrentPage(1);
    },
    [syncUrl]
  );

  const handleFiltersChange = useCallback(
    (patch) => setFilters((prev) => ({ ...prev, ...patch })),
    [setFilters]
  );

  /** Change the period on the Calls screen. Always both dates, never a bare preset. */
  const setDateRange = useCallback(
    (preset, custom) => {
      setFilters((prev) => ({
        ...prev,
        ...(preset === 'custom' && custom
          ? { dateRange: 'custom', startDate: custom.startDate, endDate: custom.endDate }
          : resolvePreset(preset, timeZone)),
      }));
    },
    [setFilters, timeZone]
  );

  /** The analytics period, shared by every dashboard-style screen. */
  const setPeriod = useCallback(
    (preset, custom) => {
      setPeriodRaw(
        preset === 'custom' && custom
          ? { dateRange: 'custom', startDate: custom.startDate, endDate: custom.endDate }
          : resolvePreset(preset, timeZone)
      );
    },
    [timeZone]
  );

  /**
   * Drill through from an aggregate to the rows behind it.
   *
   * REPLACES the filter set rather than merging into it, and always carries the
   * period it was computed over. `reason` is shown as a banner on arrival so the
   * destination states which population it is showing — "a count is a doorway",
   * and the doorway has to say where it led.
   */
  const drillTo = useCallback(
    ({ filters: drillFilters = {}, period: drillPeriod, callIds, reason, path = '/calls' }) => {
      const resolvedPeriod = drillPeriod || period;
      const next = {
        ...buildDefaultFilters(timeZone),
        ...(resolvedPeriod?.startDate
          ? {
              dateRange: resolvedPeriod.dateRange || 'custom',
              startDate: resolvedPeriod.startDate,
              endDate: resolvedPeriod.endDate,
            }
          : {}),
        ...drillFilters,
        callIds: callIds || [],
        callIdsLabel: callIds?.length ? reason || `${callIds.length} selected calls` : '',
        drillReason: reason || '',
      };
      setFiltersRaw(next);
      setCurrentPage(1);
      const search = toSearchParams(next).toString();
      hydratedFor.current = search ? `?${search}` : '';
      navigate({ pathname: path, search });
    },
    [navigate, period, timeZone]
  );

  const handleRemoveFilter = useCallback(
    (key) => {
      setFilters((prev) => {
        if (key === 'dateRange') {
          // The date chip's X used to do nothing. It now returns to the default
          // window rather than removing the bound entirely, because an unbounded
          // query over every call a tenant has ever made is not a useful default.
          return { ...prev, ...resolvePreset('last7days', timeZone) };
        }
        if (key === 'callIds') {
          return { ...prev, callIds: [], callIdsLabel: '', drillReason: '' };
        }
        if (key === 'flagged') {
          // The Outlook drill's banner describes this filter, so it goes with it.
          return { ...prev, flagged: '', drillReason: '' };
        }
        if (key === 'themeFilter') {
          return { ...prev, themeFilter: null };
        }
        if (key.startsWith('insight_')) {
          const field = key.slice('insight_'.length);
          const nextInsights = { ...prev.insightFilters };
          delete nextInsights[field];
          return { ...prev, insightFilters: nextInsights };
        }
        return { ...prev, [key]: '' };
      });
    },
    [setFilters, timeZone]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(buildDefaultFilters(timeZone));
  }, [setFilters, timeZone]);

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);
  const handleItemsPerPageChange = useCallback((n) => {
    setItemsPerPage(n);
    setCurrentPage(1);
  }, []);
  const handleSortChange = useCallback((by, dir) => {
    setSort({ by, dir });
    setCurrentPage(1);
  }, []);

  /**
   * Chips, derived from the schema so a new filter cannot be added without one.
   * The date chip is last: it is always present, and a chip that never disappears
   * should not sit in front of the ones that do.
   */
  const activeFilters = useMemo(() => {
    const chips = [];
    FILTER_SCHEMA.forEach(({ key, label }) => {
      const v = filters[key];
      if (v) chips.push({ key, label: label(v, filters) });
    });
    Object.entries(filters.insightFilters || {}).forEach(([field, v]) => {
      if (!v) return;
      // Scoring bands are stored as "score:5-9"
      const band = typeof v === 'string' && v.match(/^score:(\d+)-(\d+)$/);
      const shown = band ? (band[1] === band[2] ? `${band[1]} pts` : `${band[1]}–${band[2]} pts`) : v;
      chips.push({ key: `insight_${field}`, label: `${field.replace(/_/g, ' ')}: ${shown}` });
    });
    if (filters.themeFilter) {
      chips.push({
        key: 'themeFilter',
        label: `${filters.themeFilter.field_name || 'Theme'}: ${filters.themeFilter.name}`,
      });
    }
    if (filters.callIds?.length) {
      chips.push({
        key: 'callIds',
        label: filters.callIdsLabel || `${filters.callIds.length} selected calls`,
        tone: 'brand',
      });
    }
    chips.push({ key: 'dateRange', label: rangeLabel(filters, timeZone), tone: 'neutral' });
    return chips;
  }, [filters, timeZone]);

  /** True when anything beyond the default period is applied — for empty-state copy. */
  const hasActiveFilters = useMemo(
    () =>
      FILTER_SCHEMA.some(({ key }) => Boolean(filters[key])) ||
      Object.values(filters.insightFilters || {}).some(Boolean) ||
      Boolean(filters.callIds?.length),
    [filters]
  );

  const value = useMemo(
    () => ({
      filters,
      period,
      currentPage,
      itemsPerPage,
      sort,
      activeFilters,
      hasActiveFilters,
      setFilters,
      handleFiltersChange,
      setDateRange,
      setPeriod,
      drillTo,
      handleRemoveFilter,
      handleResetFilters,
      handlePageChange,
      handleItemsPerPageChange,
      handleSortChange,
      // Back-compat for screens not yet migrated off the old prop names.
      dashboardFilters: period,
      handleDashboardFiltersChange: (next) =>
        setPeriodRaw((prev) => ({ ...prev, ...next })),
    }),
    [
      filters, period, currentPage, itemsPerPage, sort, activeFilters, hasActiveFilters,
      setFilters, handleFiltersChange, setDateRange, setPeriod, drillTo,
      handleRemoveFilter, handleResetFilters, handlePageChange, handleItemsPerPageChange,
      handleSortChange,
    ]
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};
